import React, { useEffect, useState } from "react";
import api, { API_BASE, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Search, QrCode, ArrowRightLeft, Trash2 } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

const ASSET_CATEGORIES = [
  "computer", "laptop", "printer", "pos_machine", "server", "cctv_camera",
  "air_conditioner", "refrigerator", "shelf", "furniture", "generator", "networking",
];
const STATUSES = ["in_use", "idle", "maintenance", "disposed"];

export default function Assets() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [qrAsset, setQrAsset] = useState(null);
  const [transferAsset, setTransferAsset] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const initial = {
    name: "", category: "computer", serial_number: "", branch: "",
    assigned_to: "", purchase_date: "", purchase_cost: 0, status: "in_use",
  };
  const [form, setForm] = useState(initial);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: a }, { data: b }] = await Promise.all([api.get("/assets"), api.get("/branches")]);
        if (!cancelled) { setItems(a); setBranches(b); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/assets", { ...form, purchase_cost: parseFloat(form.purchase_cost) || 0 });
      toast.success("Asset registered");
      setOpenNew(false);
      setForm(initial);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/assets/${id}`, { status });
      toast.success(`Marked ${status.replaceAll("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    await api.delete(`/assets/${id}`);
    load();
  };

  const doTransfer = async (e) => {
    e.preventDefault();
    if (!transferTo || !transferAsset) return;
    try {
      await api.post("/asset-transfers", {
        asset_id: transferAsset.id,
        from_branch: transferAsset.branch,
        to_branch: transferTo,
        transfer_date: new Date().toISOString().slice(0, 10),
        reason: transferReason,
      });
      await api.patch(`/assets/${transferAsset.id}`, { branch: transferTo });
      toast.success("Asset transferred");
      setTransferAsset(null);
      setTransferTo("");
      setTransferReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = items.filter((i) =>
    !q || `${i.name} ${i.category} ${i.serial_number} ${i.branch} ${i.assigned_to}`.toLowerCase().includes(q.toLowerCase())
  );

  // Add auth header via fetch + blob; for simplicity, build a data URL via fetch:
  const [qrSrc, setQrSrc] = useState("");
  useEffect(() => {
    if (!qrAsset) return undefined;
    let cancelled = false;
    (async () => {
      const token = getToken();
      const res = await fetch(`${API_BASE}/assets/${qrAsset.id}/qr`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const blob = await res.blob();
      if (!cancelled) setQrSrc(URL.createObjectURL(blob));
    })();
    return () => {
      cancelled = true;
      setQrSrc("");
    };
  }, [qrAsset]);

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Asset Management"
        subtitle="Registry with QR tagging, branch transfers, assignments and disposal lifecycle."
        actions={
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="asset-new-button">
                <Plus size={16} className="mr-1.5" /> Register asset
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Register asset</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="asset-form">
                <div>
                  <Label>Asset name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1" data-testid="asset-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1" data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ASSET_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c.replaceAll("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Serial / Tag #</Label>
                    <Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                      className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Branch</Label>
                    <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                      <SelectTrigger className="mt-1" data-testid="asset-branch-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Assigned to</Label>
                    <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                      className="mt-1" />
                  </div>
                  <div>
                    <Label>Purchase date</Label>
                    <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                      className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Purchase cost (UGX)</Label>
                  <Input type="number" value={form.purchase_cost}
                    onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })}
                    className="mt-1" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="asset-submit-button">
                    {busy ? "Saving…" : "Register"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search assets…" value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9" data-testid="asset-search-input" />
        </div>
        <span className="text-xs text-gray-500">{filtered.length} record(s)</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No assets registered yet" hint="Register your first asset above." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="assets-table">
              <thead className="bg-gray-50">
                <tr>
                  {["Asset", "Category", "Serial #", "Branch", "Assigned to", "Status", ""].map((h) => (
                    <th key={h} className="text-left text-xs font-bold text-gray-600 uppercase tracking-wider px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{a.name}</td>
                    <td className="px-4 py-3 text-gray-700">{(a.category || "").replaceAll("_", " ")}</td>
                    <td className="px-4 py-3 text-gray-700 font-mono text-xs">{a.serial_number || "—"}</td>
                    <td className="px-4 py-3 text-gray-700">{a.branch}</td>
                    <td className="px-4 py-3 text-gray-700">{a.assigned_to || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge tone={a.status === "in_use" ? "emerald" : a.status === "maintenance" ? "amber" : a.status === "disposed" ? "gray" : "blue"}>
                        {(a.status || "").replaceAll("_", " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`asset-actions-${a.id}`}>
                            <MoreHorizontal size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setQrAsset(a)}>
                            <QrCode size={13} className="mr-2" /> Show QR
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTransferAsset(a)}>
                            <ArrowRightLeft size={13} className="mr-2" /> Transfer
                          </DropdownMenuItem>
                          {STATUSES.map((s) => (
                            <DropdownMenuItem key={s} onClick={() => updateStatus(a.id, s)}>
                              Mark {s.replaceAll("_", " ")}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuItem onClick={() => remove(a.id)} className="text-red-700">
                            <Trash2 size={13} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QR dialog */}
      <Dialog open={!!qrAsset} onOpenChange={(v) => !v && setQrAsset(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Asset QR tag</DialogTitle></DialogHeader>
          {qrAsset && (
            <div className="text-center">
              {qrSrc ? <img src={qrSrc} alt="QR" className="mx-auto h-56 w-56" /> : <div className="h-56 flex items-center justify-center text-sm text-gray-500">Generating…</div>}
              <div className="mt-3 text-sm font-semibold text-gray-900">{qrAsset.name}</div>
              <div className="text-xs text-gray-500">{qrAsset.serial_number || "no serial"} · {qrAsset.branch}</div>
              <Button className="mt-4 w-full bg-emerald-900 hover:bg-emerald-950 text-white"
                onClick={() => { const a = document.createElement("a"); a.href = qrSrc; a.download = `qr-${qrAsset.id}.png`; a.click(); }}>
                Download PNG
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={!!transferAsset} onOpenChange={(v) => !v && setTransferAsset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Transfer asset</DialogTitle></DialogHeader>
          {transferAsset && (
            <form onSubmit={doTransfer} className="space-y-3">
              <div className="bg-gray-50 rounded-md p-3 text-sm">
                <div className="font-medium text-gray-900">{transferAsset.name}</div>
                <div className="text-xs text-gray-600">From: <span className="font-semibold">{transferAsset.branch}</span></div>
              </div>
              <div>
                <Label>Transfer to branch</Label>
                <Select value={transferTo} onValueChange={setTransferTo}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Choose destination" /></SelectTrigger>
                  <SelectContent>
                    {branches.filter((b) => b.name !== transferAsset.branch).map((b) => (
                      <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason</Label>
                <Input value={transferReason} onChange={(e) => setTransferReason(e.target.value)} className="mt-1" />
              </div>
              <Button type="submit" disabled={!transferTo}
                className="w-full bg-emerald-900 hover:bg-emerald-950 text-white">
                Confirm transfer
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
